"use client";

import {
	asArray,
	asObject,
	asString,
	TemplatePreview,
} from "@commerce/channels/whatsapp-preview/WhatsAppPreview";
import type { InferRouterInputs, InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { errorMessage } from "@saas/settings/lib/errors";
import { orpc } from "@shared/lib/orpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { Field, FieldLabel } from "@ui/components/field";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@ui/components/sheet";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { Textarea } from "@ui/components/textarea";
import { toast } from "@ui/components/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import {
	FileTextIcon,
	MapPinIcon,
	PackageIcon,
	PlusIcon,
	ShoppingCartIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { ConversationDetail } from "../types";

type SendInput =
	InferRouterInputs<ApiRouter>["conversations"]["send"]["message"];
type TemplateRow =
	InferRouterOutputs<ApiRouter>["channels"]["templates"]["list"]["templates"][number];

/** Staff sends beyond text: one mutation, one toast, the transcript refreshes from the event. */
function useStaffSend(conversationId: string, onSent: () => void) {
	const queryClient = useQueryClient();
	return useMutation(
		orpc.conversations.send.mutationOptions({
			onSuccess: (sent) => {
				toast.add({
					type: "success",
					title: "Sent on WhatsApp",
					description:
						sent.costUsd > 0
							? `Meta bills about $${sent.costUsd.toFixed(4)} for it.`
							: undefined,
				});
				onSent();
			},
			onError: (error) =>
				toast.add({
					type: "error",
					title: "Not sent",
					description: errorMessage(error, "Try again in a moment."),
				}),
			onSettled: () =>
				queryClient.invalidateQueries(
					{
						queryKey: orpc.conversations.get.key({
							input: { conversationId },
						}),
					},
					{ cancelRefetch: false },
				),
		}),
	);
}

type Open = "products" | "template" | "location" | "checkout" | null;

/**
 * The composer's "+": product cards, a template, a branch's location or the checkout, all
 * sent through the same path as the agent's replies (`conversations.send`).
 */
export function SendMenu({ detail }: { detail: ConversationDetail }) {
	const [open, setOpen] = useState<Open>(null);
	const close = () => setOpen(null);
	return (
		<>
			<DropdownMenu>
				<Tooltip>
					<TooltipTrigger
						render={
							<DropdownMenuTrigger
								render={
									<Button
										type="button"
										variant="outline"
										size="icon"
										aria-label="Send products, a template or a location"
									/>
								}
							/>
						}
					>
						<PlusIcon />
					</TooltipTrigger>
					<TooltipContent>
						Send products, a template or a location
					</TooltipContent>
				</Tooltip>
				<DropdownMenuContent align="start" side="top" className="w-56">
					<DropdownMenuGroup>
						<DropdownMenuItem onClick={() => setOpen("products")}>
							<PackageIcon />
							Products
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setOpen("template")}>
							<FileTextIcon />
							Template
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setOpen("location")}>
							<MapPinIcon />
							Branch location
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setOpen("checkout")}>
							<ShoppingCartIcon />
							Checkout
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenuContent>
			</DropdownMenu>
			{/* Mounted only while open: nothing is fetched until a dialog is used. */}
			{open === "products" ? (
				<ProductsDialog detail={detail} open onClose={close} />
			) : open === "template" ? (
				<TemplateSheet detail={detail} open onClose={close} />
			) : open === "location" ? (
				<LocationDialog detail={detail} open onClose={close} />
			) : open === "checkout" ? (
				<CheckoutDialog detail={detail} open onClose={close} />
			) : null}
		</>
	);
}

/** Outside the 24-hour window only a template reaches the shopper. */
export function SendTemplateButton({ detail }: { detail: ConversationDetail }) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Button size="sm" variant="outline" onClick={() => setOpen(true)}>
				<FileTextIcon data-icon="inline-start" />
				Send a template
			</Button>
			{open ? (
				<TemplateSheet
					detail={detail}
					open
					onClose={() => setOpen(false)}
				/>
			) : null}
		</>
	);
}

/** "Send checkout" in the details panel: the same confirm as the composer's. */
export function SendCheckoutButton({ detail }: { detail: ConversationDetail }) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Button size="sm" variant="outline" onClick={() => setOpen(true)}>
				<ShoppingCartIcon data-icon="inline-start" />
				Send checkout
			</Button>
			{open ? (
				<CheckoutDialog
					detail={detail}
					open
					onClose={() => setOpen(false)}
				/>
			) : null}
		</>
	);
}

function ProductsDialog({
	detail,
	open,
	onClose,
}: {
	detail: ConversationDetail;
	open: boolean;
	onClose: () => void;
}) {
	const id = useId();
	const [q, setQ] = useState("");
	const [picked, setPicked] = useState<Map<string, string>>(new Map());
	const [text, setText] = useState("");
	const term = q.trim();
	const found = useQuery({
		...orpc.catalog.products.find.queryOptions({
			input: { organizationId: detail.organizationId, q: term },
		}),
		enabled: open && term.length >= 2,
	});
	const reset = () => {
		setQ("");
		setPicked(new Map());
		setText("");
		onClose();
	};
	const send = useStaffSend(detail.id, reset);
	const toggle = (productId: string, title: string) =>
		setPicked((current) => {
			const next = new Map(current);
			if (next.has(productId)) {
				next.delete(productId);
			} else if (next.size < 30) {
				next.set(productId, title);
			}
			return next;
		});

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? null : reset())}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Send products</DialogTitle>
					<DialogDescription>
						Products the conversation's restrictions hide are
						refused.
					</DialogDescription>
				</DialogHeader>
				<Field>
					<FieldLabel htmlFor={`${id}-q`}>Find a product</FieldLabel>
					<Input
						id={`${id}-q`}
						value={q}
						onChange={(event) => setQ(event.target.value)}
						placeholder="Almarai milk, or a barcode"
						autoFocus
					/>
				</Field>
				<ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
					{found.isFetching && !found.data ? (
						Array.from({ length: 3 }).map((_, i) => (
							<li key={i}>
								<Skeleton className="h-9 w-full rounded-xl" />
							</li>
						))
					) : (found.data ?? []).length === 0 && term.length >= 2 ? (
						<li className="px-2 py-3 text-muted-foreground text-sm">
							Nothing matches “{term}”.
						</li>
					) : (
						(found.data ?? []).map((product) => (
							<li key={product.id}>
								<label
									htmlFor={`${id}-p-${product.id}`}
									className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 hover:bg-muted"
								>
									<Checkbox
										id={`${id}-p-${product.id}`}
										checked={picked.has(product.id)}
										onCheckedChange={() =>
											toggle(product.id, product.title)
										}
										disabled={!product.active}
									/>
									<span className="min-w-0 flex-1">
										<span
											dir="auto"
											className="block truncate text-left text-sm"
										>
											{product.title}
										</span>
										<span className="block truncate text-muted-foreground text-xs">
											{[product.brand, product.department]
												.filter(Boolean)
												.join(" · ") ||
												(product.active
													? ""
													: "Hidden")}
										</span>
									</span>
								</label>
							</li>
						))
					)}
				</ul>
				<Field>
					<FieldLabel htmlFor={`${id}-text`}>
						Message (optional)
					</FieldLabel>
					<Textarea
						id={`${id}-text`}
						dir="auto"
						value={text}
						onChange={(event) => setText(event.target.value)}
						maxLength={1000}
						rows={2}
						placeholder="Here are the ones we have today"
					/>
				</Field>
				<DialogFooter>
					<Button variant="ghost" onClick={reset}>
						Cancel
					</Button>
					<DisabledReason
						reason={picked.size ? null : "Pick a product first"}
					>
						<Button
							disabled={!picked.size || send.isPending}
							onClick={() =>
								send.mutate({
									conversationId: detail.id,
									message: {
										kind: "products",
										productIds: [...picked.keys()],
										...(text.trim()
											? { text: text.trim() }
											: {}),
									},
								})
							}
						>
							{send.isPending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							Send {picked.size ? picked.size : ""}{" "}
							{picked.size === 1 ? "product" : "products"}
						</Button>
					</DisabledReason>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/** `{{1}}`, `{{2}}`… of a template's body, in order. */
export function templateParams(components: unknown): string[] {
	const body = asArray(components)
		.map(asObject)
		.find((component) => String(component.type).toUpperCase() === "BODY");
	const text = asString(body?.text) ?? "";
	return [
		...new Set(
			[...text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1] ?? ""),
		),
	];
}

function TemplateSheet({
	detail,
	open,
	onClose,
}: {
	detail: ConversationDetail;
	open: boolean;
	onClose: () => void;
}) {
	const id = useId();
	const templates = useQuery({
		...orpc.channels.templates.list.queryOptions({
			input: {
				organizationId: detail.organizationId,
				status: "APPROVED",
			},
		}),
		enabled: open,
	});
	const [templateId, setTemplateId] = useState<string | null>(null);
	const [values, setValues] = useState<Record<string, string>>({});
	const reset = () => {
		setTemplateId(null);
		setValues({});
		onClose();
	};
	const send = useStaffSend(detail.id, reset);
	// Marketing goes only to a shopper who opted in after any opt-out; the server refuses it too.
	const approved = (templates.data?.templates ?? []).filter(
		(t) =>
			detail.marketingAllowed || t.category.toUpperCase() !== "MARKETING",
	);
	const template = approved.find((t) => t.id === templateId) ?? null;
	const params = template ? templateParams(template.components) : [];
	const missing = params.filter((name) => !values[name]?.trim());

	return (
		<Sheet open={open} onOpenChange={(next) => (next ? null : reset())}>
			<SheetContent className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md">
				<SheetHeader>
					<SheetTitle>Send a template</SheetTitle>
					<SheetDescription>
						Templates reach the shopper outside the 24-hour window.
						Meta bills each one at its category's rate.
						{detail.marketingAllowed
							? null
							: " Marketing templates are hidden: this shopper has not agreed to them."}
					</SheetDescription>
				</SheetHeader>
				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
					<TemplatePicker
						id={`${id}-template`}
						templates={templates}
						approved={approved}
						value={templateId}
						onChange={(value) => {
							setTemplateId(value);
							setValues({});
						}}
					/>
					{params.map((name) => (
						<Field key={name}>
							<FieldLabel
								htmlFor={`${id}-${name}`}
							>{`Value {{${name}}}`}</FieldLabel>
							<Input
								id={`${id}-${name}`}
								dir="auto"
								value={values[name] ?? ""}
								onChange={(event) =>
									setValues((current) => ({
										...current,
										[name]: event.target.value,
									}))
								}
							/>
						</Field>
					))}
					{template ? (
						<TemplateOutcome
							template={template}
							params={params}
							values={values}
						/>
					) : null}
				</div>
				<SheetFooter className="border-border/70 border-t">
					<DisabledReason reason={sendBlocker(template, missing)}>
						<Button
							className="w-full"
							disabled={
								!template ||
								missing.length > 0 ||
								send.isPending
							}
							onClick={() =>
								template &&
								send.mutate({
									conversationId: detail.id,
									message: {
										kind: "template",
										templateId: template.id,
										values,
									} satisfies SendInput,
								})
							}
						>
							{send.isPending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							Send the template
						</Button>
					</DisabledReason>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}

/** Why the template cannot go yet, or null when it can. */
function sendBlocker(
	template: TemplateRow | null,
	missing: readonly string[],
): string | null {
	if (!template) {
		return "Pick a template first";
	}
	return missing.length ? "Fill in every value" : null;
}

/** The approved templates to choose from, or why there are none to show. */
function TemplatePicker({
	id,
	templates,
	approved,
	value,
	onChange,
}: {
	id: string;
	templates: { isPending: boolean; isError: boolean; error: unknown };
	approved: readonly TemplateRow[];
	value: string | null;
	onChange: (value: string | null) => void;
}) {
	if (templates.isPending) {
		return <Skeleton className="h-9 w-full rounded-2xl" />;
	}
	if (templates.isError) {
		return (
			<p className="text-muted-foreground text-sm">
				{errorMessage(
					templates.error,
					"Templates could not be loaded. An admin can check them on the Channels page.",
				)}
			</p>
		);
	}
	if (approved.length === 0) {
		return (
			<p className="text-muted-foreground text-sm">
				No approved templates yet. An admin creates them under Channels,
				Templates.
			</p>
		);
	}
	return (
		<Field>
			<FieldLabel htmlFor={id}>Template</FieldLabel>
			<Select
				items={Object.fromEntries(approved.map((t) => [t.id, t.name]))}
				value={value}
				onValueChange={onChange}
			>
				<SelectTrigger id={id} className="w-full">
					<SelectValue placeholder="Pick a template" />
				</SelectTrigger>
				<SelectContent>
					{approved.map((t) => (
						<SelectItem key={t.id} value={t.id}>
							{t.name} · {t.language} · {t.category.toLowerCase()}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</Field>
	);
}

/** What the shopper gets with the values filled in so far, and what Meta bills for it. */
function TemplateOutcome({
	template,
	params,
	values,
}: {
	template: TemplateRow;
	params: readonly string[];
	values: Record<string, string>;
}) {
	return (
		<div className="flex flex-col gap-2">
			<p className="font-medium text-muted-foreground text-xs">
				The shopper gets
			</p>
			<TemplatePreview
				payload={{
					template: {
						name: template.name,
						language: { code: template.language },
						components: [
							{
								type: "body",
								parameters: params.map((name) => ({
									type: "text",
									text: values[name] || `{{${name}}}`,
								})),
							},
						],
					},
				}}
			/>
			<p className="text-muted-foreground text-xs">
				Billed by Meta at the {template.category.toLowerCase()} rate for
				the shopper's country.
			</p>
		</div>
	);
}

function LocationDialog({
	detail,
	open,
	onClose,
}: {
	detail: ConversationDetail;
	open: boolean;
	onClose: () => void;
}) {
	const id = useId();
	const stores = useQuery({
		...orpc.catalog.overview.queryOptions({
			input: { organizationId: detail.organizationId },
		}),
		enabled: open,
		select: (overview) => overview.stores,
	});
	const [storeId, setStoreId] = useState<string | null>(detail.store.id);
	const send = useStaffSend(detail.id, onClose);
	const list = stores.data ?? [];
	return (
		<Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Send a branch location</DialogTitle>
					<DialogDescription>
						The shopper gets the branch's pin and address.
					</DialogDescription>
				</DialogHeader>
				{stores.isPending ? (
					<Skeleton className="h-9 w-full rounded-2xl" />
				) : (
					<Field>
						<FieldLabel htmlFor={`${id}-store`}>Branch</FieldLabel>
						<Select
							items={Object.fromEntries(
								list.map((s) => [s.id, s.name]),
							)}
							value={storeId}
							onValueChange={setStoreId}
						>
							<SelectTrigger
								id={`${id}-store`}
								className="w-full"
							>
								<SelectValue placeholder="Pick a branch" />
							</SelectTrigger>
							<SelectContent>
								{list.map((store) => (
									<SelectItem key={store.id} value={store.id}>
										{store.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</Field>
				)}
				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						disabled={!storeId || send.isPending}
						onClick={() =>
							storeId &&
							send.mutate({
								conversationId: detail.id,
								message: { kind: "location", storeId },
							})
						}
					>
						{send.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Send the location
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function CheckoutDialog({
	detail,
	open,
	onClose,
}: {
	detail: ConversationDetail;
	open: boolean;
	onClose: () => void;
}) {
	const send = useStaffSend(detail.id, onClose);
	const items = detail.basket.itemCount;
	return (
		<Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Send the checkout</DialogTitle>
					<DialogDescription>
						{items
							? `The shopper gets the checkout form for their basket of ${items} ${items === 1 ? "item" : "items"}. Nothing is ordered until they confirm it.`
							: "The basket is empty, so there is nothing to check out yet."}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<DisabledReason
						reason={items ? null : "The basket is empty"}
					>
						<Button
							disabled={!items || send.isPending}
							onClick={() =>
								send.mutate({
									conversationId: detail.id,
									message: { kind: "checkout" },
								})
							}
						>
							{send.isPending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							Send the checkout
						</Button>
					</DisabledReason>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
